import React, { useEffect, useState } from "react";
import { SelectButton } from "primereact/selectbutton";
import SelectedDatasetElement from "../SelectedDatasetElement";
import { Axis, AxisAssignedArrays, Dataset, DatasetAxis } from "../models/types";
import "./styles.css";
import {
  colorAxisOptions,
  daysArray,
  hoursArray,
  initialColorAxisDataset,
  monthsArray,
  offsetOptions,
  options,
  weeksArray,
  yearsArray,
} from "components/dashboard/charts/utils";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";

interface ScatterData {
  data: string[];
  identifier: string;
  title: string;
  unit: string;
  value?: string[] | number[];
  scatterSelectedTimeOffset?: string;
}

const MultiDragArea = ({
  selectedDatasetsArr,
  removeDataset,
  enableXAxis,
  assignToMultiAxis,
  colorScale,
  currentScatterData,
  scatterSelectedTimeOffset,
  data,
  setCurrentScatterData,
  setScatterSelectedTimeOffset,
  setColorScale,
  handleUnselectColorDataset,
  setColorAxisID,
}: {
  selectedDatasetsArr: Array<Dataset>;
  removeDataset: (datasetId: string) => void;
  enableXAxis: boolean;
  assignToMultiAxis: (
    xDataset: string,
    yDataset: string,
    colorDataset: string,
    sizeDataset: string,
    colorScale: boolean
  ) => void;

  colorScale: boolean;

  currentScatterData: ScatterData[];

  scatterSelectedTimeOffset: string | undefined;
  data: DatasetDataInfoResponse[] | undefined;
  setCurrentScatterData: (val: ScatterData[]) => void;
  setScatterSelectedTimeOffset: (val: string | undefined) => void;
  setColorScale: (val: boolean) => void;
  handleUnselectColorDataset: () => void;
  setColorAxisID: (val: string) => void;
}) => {
  const [datasetsWithMultiAxis, setDatasetsWithMultiAxis] = useState<Array<DatasetAxis>>([]);
  const [multiAxisArray, setMultiAxisArray] = useState<AxisAssignedArrays>({
    none: [],
    X: [],
    Y: [],
    size: [],
    color: [],
  });
  const [fieldXClassName, setFieldXClassName] = useState<string>("empty");
  const [fieldYClassName, setFieldYClassName] = useState<string>("empty");
  const [colorAxisOption, setColorAxisOption] = useState<boolean>(true);

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    e.dataTransfer.setData("id", id);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (!scatterSelectedTimeOffset || colorAxisOption) return;

    const newData = [...currentScatterData];
    const colorAxisData = newData[2] || { ...initialColorAxisDataset };

    if (currentScatterData[0]?.data?.length !== colorAxisData?.data?.length) {
      colorAxisData.data = currentScatterData[0]?.data;
      colorAxisData.identifier = currentScatterData[0]?.identifier;
      setColorAxisID(colorAxisData.identifier);
    }
    switch (scatterSelectedTimeOffset) {
      case "HOURS":
        colorAxisData.value = hoursArray;
        break;
      case "DAYS":
        colorAxisData.value = daysArray;
        break;
      case "WEEKS":
        colorAxisData.value = weeksArray;
        break;
      case "MONTHS":
        colorAxisData.value = monthsArray;
        break;
      case "YEARS":
        colorAxisData.value = yearsArray;
        break;
      default:
        break;
    }

    colorAxisData.scatterSelectedTimeOffset = scatterSelectedTimeOffset;
    newData[2] = colorAxisData;
    setCurrentScatterData(newData);
  }, [scatterSelectedTimeOffset, colorAxisOption, data]);

  const onDropWithSwitch = (e: React.DragEvent<HTMLDivElement>, targetAxis: Axis) => {
    const id = e.dataTransfer.getData("id");
    const dts = datasetsWithMultiAxis.map(dt => {
      if (dt.identifier === id && targetAxis === "none") {
        return { ...dt, axis: targetAxis };
      }
      if (dt.identifier === id) {
        const currentAxis = dt.axis;
        const dtTargetAxisIndex = datasetsWithMultiAxis.findIndex(dta => dta.axis === targetAxis);
        const updatedDt = { ...dt, axis: targetAxis };

        if (dtTargetAxisIndex !== -1) {
          const updatedDtTarget = {
            ...datasetsWithMultiAxis[dtTargetAxisIndex],
            axis: currentAxis,
          };
          datasetsWithMultiAxis[dtTargetAxisIndex] = updatedDtTarget;
        }

        return updatedDt;
      }

      return dt;
    });
    setDatasetsWithMultiAxis(dts);
  };

  useEffect(() => {
    setDatasetsWithMultiAxis(
      selectedDatasetsArr.reduce((res: Array<DatasetAxis>, curr, index) => {
        switch (index) {
          case 0:
            res.push({ ...curr, axis: "X" });
            break;
          case 1:
            res.push({ ...curr, axis: "Y" });
            break;
          case 2:
            if (!scatterSelectedTimeOffset) res.push({ ...curr, axis: "color" });
            else res.push({ ...curr, axis: "size" });
            break;
          case 3:
            if (!scatterSelectedTimeOffset) res.push({ ...curr, axis: "size" });
            else res.push({ ...curr, axis: "none" });
            break;
          default:
            res.push({ ...curr, axis: "none" });
        }
        return res;
      }, [])
    );
  }, [selectedDatasetsArr, scatterSelectedTimeOffset]);

  useEffect(() => {
    setMultiAxisArray({ none: [], X: [], Y: [], size: [], color: [] });
    const tempArr: AxisAssignedArrays = { none: [], X: [], Y: [], size: [], color: [] };
    datasetsWithMultiAxis.forEach(d => {
      tempArr[d.axis]?.push(
        <SelectedDatasetElement
          key={d.identifier}
          selectedDataset={d}
          removeDataset={removeDataset}
          dragStart={onDragStart}
        />
      );
    });

    if (tempArr.X != null && tempArr.Y != null) {
      const xDataset: string = tempArr.X[0]?.props.selectedDataset.identifier;
      const yDataset: string = tempArr.Y[0]?.props.selectedDataset.identifier;
      let colorDataset: string = scatterSelectedTimeOffset ? currentScatterData[0].identifier : "";
      let sizeDataset: string = "";

      if (tempArr.color && tempArr.color?.length > 0) {
        colorDataset = tempArr.color[0].props.selectedDataset.identifier;
      }
      if (tempArr.size && tempArr.size?.length > 0) {
        sizeDataset = tempArr.size[0].props.selectedDataset.identifier;
      }
      assignToMultiAxis(xDataset, yDataset, colorDataset, sizeDataset, colorScale);
      if (colorDataset === "" && scatterSelectedTimeOffset === undefined) {
        setColorScale(true);
      }
      setMultiAxisArray(tempArr);
    }
  }, [datasetsWithMultiAxis]);

  useEffect(() => {
    const foundXIndex = datasetsWithMultiAxis.findIndex(d => d.axis === "X");
    const foundYIndex = datasetsWithMultiAxis.findIndex(d => d.axis === "Y");
    setFieldXClassName(foundXIndex !== -1 ? "" : "empty");
    setFieldYClassName(foundYIndex !== -1 ? "" : "empty");
  }, [datasetsWithMultiAxis]);

  const handleColorAxisOptionChange = (value: boolean) => {
    setColorAxisOption(value);
    setColorScale(value);
    if (value) {
      currentScatterData.splice(2, 1);
      handleUnselectColorDataset();
      setScatterSelectedTimeOffset(undefined);
    } else {
      setScatterSelectedTimeOffset("HOURS");
      handleUnselectColorDataset();
      setColorAxisID(currentScatterData[0].identifier);
    }
  };

  return (
    <div className="padding flex gap-3">
      <div>
        <label htmlFor="multi-select">Selected multi datasets: </label>
        <div
          className="drop-box"
          id="multi-select"
          onDragOver={e => onDragOver(e)}
          onDrop={e => onDropWithSwitch(e, "none")}
        >
          {multiAxisArray.none}
        </div>
      </div>
      <div className="flexColumn">
        {enableXAxis && (
          <div className="" id="dropbox">
            <label htmlFor="dropboxX">X axis: </label>
            <div
              className={`drop-box ${fieldXClassName}`}
              id="dropboxX"
              onDragOver={e => onDragOver(e)}
              onDrop={e => onDropWithSwitch(e, "X")}
            >
              {multiAxisArray.X}
            </div>
          </div>
        )}
        <div className="" id="dropbox">
          <label htmlFor="dropboxY">Y axis: </label>
          <div
            className={`drop-box ${fieldYClassName}`}
            id="dropboxY"
            onDragOver={e => onDragOver(e)}
            onDrop={e => onDropWithSwitch(e, "Y")}
          >
            {multiAxisArray.Y}
          </div>
        </div>
      </div>
      <div className="flexColumn">
        <div className="flex align-items-center">
          <div id="dropbox">
            <div className="flex flex-row">              
              <label htmlFor="dropboxColor" style={{width: "100px"}}>Color axis: </label>
              <SelectButton
                value={colorAxisOption}
                options={colorAxisOptions}
                onChange={e => handleColorAxisOptionChange(e.value)}
                optionLabel="name"
                style={{ whiteSpace: "nowrap", transform: "scale(80%)", padding: "0 0px 0 28px" }}
                disabled={currentScatterData?.length < 2}
              />
            </div>
            {!colorAxisOption && (
              <div style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                <SelectButton
                  value={scatterSelectedTimeOffset}
                  options={offsetOptions}
                  onChange={e => setScatterSelectedTimeOffset(e.value)}
                  className="select-button-class"
                  disabled={currentScatterData?.length < 2}
                />
              </div>
            )}
            {colorAxisOption && (
              <div
                className="drop-box"
                id="dropboxColor"
                onDragOver={e => onDragOver(e)}
                onDrop={e => onDropWithSwitch(e, "color")}
              >
                {multiAxisArray.color}
              </div>
            )}
          </div>
          {colorAxisOption && (
            <div className="pt-6">
              <SelectButton
                value={colorScale}
                options={options}
                onChange={e => setColorScale(e.value)}
                optionLabel="name"
                style={{ whiteSpace: "nowrap", transform: "scale(85%)" }}
                disabled={multiAxisArray.color?.length === 0}
              />
            </div>
          )}
        </div>

        <div className="" id="dropbox">
          <label htmlFor="dropboxSize">Size axis: </label>
          <div
            className="drop-box"
            id="dropboxsize"
            onDragOver={e => onDragOver(e)}
            onDrop={e => onDropWithSwitch(e, "size")}
          >
            {multiAxisArray.size}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiDragArea;
